import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApiError, authApi } from '../api';
import { finalizeTokens } from '../session-helpers';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token?: string }>();
  const [search] = useSearchParams();
  const presetEmail = search.get('email') ?? '';

  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>(
    token ? 'verifying' : 'idle',
  );
  const [message, setMessage] = useState<string>('');

  const [email, setEmail] = useState(presetEmail);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const tokens = await authApi.verifyEmailByLink(token);
        await finalizeTokens(tokens);
        setStatus('success');
        setTimeout(() => navigate('/onboarding', { replace: true }), 1000);
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof ApiError ? err.message : 'Verification failed');
      }
    })();
  }, [token, navigate]);

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      const tokens = await authApi.verifyEmailByCode(email, code);
      await finalizeTokens(tokens);
      setStatus('success');
      setTimeout(() => navigate('/onboarding', { replace: true }), 500);
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof ApiError ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (!email) return;
    try {
      await authApi.resendVerification(email);
      setMessage('A new verification email is on the way.');
    } catch {
      setMessage('Could not resend — please try again shortly.');
    }
  };

  if (token && status === 'verifying') {
    return <Centered title="Verifying your email…" />;
  }
  if (status === 'success') {
    return <Centered title="Verified ✓" body="Taking you to onboarding…" />;
  }

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="card w-full max-w-sm">
        <h1 className="text-xl font-semibold">Verify your email</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter the 6-digit code from your inbox, or click the link in the email.
        </p>

        <form className="mt-6 space-y-4" onSubmit={submitCode}>
          <div>
            <label className="block text-sm font-medium">Email</label>
            <input
              type="email"
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Code</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              className="input mt-1 text-center font-mono tracking-[0.5em]"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
          </div>
          {message && (
            <p
              className={
                status === 'error'
                  ? 'rounded bg-danger/10 px-3 py-2 text-xs text-danger'
                  : 'text-xs text-muted-foreground'
              }
            >
              {message}
            </p>
          )}
          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
        </form>
        <div className="mt-4 flex justify-between text-sm">
          <button onClick={resend} className="text-primary hover:underline">
            Resend email
          </button>
          <Link to="/login" className="text-muted-foreground hover:underline">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}

function Centered({ title, body }: { title: string; body?: string }) {
  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="card w-full max-w-sm text-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        {body && <p className="mt-2 text-sm text-muted-foreground">{body}</p>}
      </div>
    </div>
  );
}
